import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { SEARCH_KEY_ROUTE } from '../layout/navConfig';
import type { SearchResult } from '../lib/types';
import { Spinner } from './ui';

export function GlobalSearch({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [result, setResult] = useState<SearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const seq = useRef(0);

  useEffect(() => {
    if (open) {
      setQ('');
      setResult(null);
      setError(null);
      setTimeout(() => inputRef.current?.focus(), 20);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const trimmed = q.trim();
    if (!trimmed) { setResult(null); return; }
    const my = ++seq.current;
    const t = setTimeout(() => {
      setLoading(true);
      setError(null);
      api.detail<SearchResult>(`/api/search?q=${encodeURIComponent(trimmed)}`)
        .then((r) => { if (my === seq.current) setResult(r); })
        .catch((e) => { if (my === seq.current) setError(e?.message || '搜索失败'); })
        .finally(() => { if (my === seq.current) setLoading(false); });
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const go = (key: string, query: string) => {
    const route = SEARCH_KEY_ROUTE[key];
    if (!route) return;
    onClose();
    navigate(`${route}?q=${encodeURIComponent(query)}`);
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="cmd" onClick={(e) => e.stopPropagation()}>
        <div className="cmd-top">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--ink-3)" strokeWidth="2" aria-hidden="true">
            <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" strokeLinecap="round" />
          </svg>
          <input
            ref={inputRef}
            value={q}
            placeholder="全局搜索：合同号 / 工单 / 客户 / 物料 / 序列号 / 报价 / 发票…"
            onChange={(e) => setQ(e.target.value)}
          />
          <kbd style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--ink-3)', border: '1px solid var(--line)', borderRadius: 4, padding: '1px 6px' }}>Esc</kbd>
        </div>
        <div className="cmd-body">
          {loading ? <div style={{ padding: 16 }}><Spinner label="正在检索…" /></div> : null}
          {error ? <div className="notice bad" style={{ margin: 12 }}>{error}</div> : null}
          {!loading && !error && q.trim() && result && result.groups.length === 0 ? (
            <div className="empty">没有匹配 “{q.trim()}” 的记录。</div>
          ) : null}
          {!loading && result?.groups.map((g) => (
            <div key={g.key}>
              <div className="cmd-group-label">{g.label}</div>
              {g.items.map((it) => (
                <div className="cmd-item" key={`${g.key}-${it.id}`} onClick={() => go(g.key, it.query || it.title)}>
                  <div style={{ minWidth: 0 }}>
                    <div className="t">{it.title}</div>
                    {it.subtitle ? <div className="s">{it.subtitle}</div> : null}
                  </div>
                  {it.meta ? <span className="m">{it.meta}</span> : null}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
